/**
 * THE EDITOR — the screen the cabinet has been missing.
 *
 * ═══════════════════════════════════════════════════════════════════════════════════════════════
 * ★★ WHAT WAS WRONG BEFORE THIS FILE, MEASURED RATHER THAN ASSERTED
 * ═══════════════════════════════════════════════════════════════════════════════════════════════
 * `CABINET_ROUTES` held five entries and none of them was an editor
 * (features/cabinet/routes.ts:48-92). `ArticlesRoute` calls `createDraft` and took a `editorHref`
 * prop whose own comment read *"This track does not own that route, so the link is handed in"* —
 * and nothing handed one in, so an author who pressed "Start a new draft" got a row in a table and
 * no way to open it. Twelve of the sixty-six operations had NO CALLER ANYWHERE UNDER
 * `web/` — measured with `git grep -il <id> HEAD -- web/`, which returned zero files for each of
 * `getOwnArticle`, `autosaveArticle`, `submitArticle`, `resubmitArticle`, `getStructuralRules`,
 * `suggestTags`, `getPublishDateSuggestions`, `checkArticleSlugAvailability`,
 * `claimArticleImageUpload`, `mintDraftPreviewLink`, `getArticleReview` and `resolveReviewFinding`.
 * (`listEditorCategories` is the thirteenth and is the one exception: the application form already
 * calls it, `features/application/data/useApplicationData.ts:71`.)
 *
 * ★ THE ADDRESS IS OURS AND IS NOT THE OWNER'S TO RULE — decision-log rows 99, 100 and 108,
 * restated at `09-authors-app.md:23-25`: *"layout, wording, navigation and screen flow are the
 * app's own business, settled in the build and never brought to the owner"*. `/articles/$articleId/
 * edit` sits under the list it is reached from, which is the only thing that argues for it.
 *
 * ═══════════════════════════════════════════════════════════════════════════════════════════════
 * ★★ THE THREE WRITE RULES ARE A STATE OF THIS SCREEN, NOT AN ERROR
 * ═══════════════════════════════════════════════════════════════════════════════════════════════
 * G19: the author writes in `draft` and `changes_requested`; **nobody** writes in `in_review`,
 * `scheduled`, `unpublished` or `archived`; an admin only in `published`
 * (authors-api.yaml:1005-1007). `WorkingCopy.writableBy` says which applies, so the editor OPENS on
 * an article in review and shows it read-only rather than refusing to open it — an author who
 * submitted something and wants to re-read it should not be told no.
 *
 * ★ SUBMIT SAVES FIRST, ALWAYS. `submitArticle` takes no request body: *"the button seals what the
 * author is looking at, and what the author is looking at is the working copy the editor has been
 * autosaving all along"* (authors-api.d.ts:505). If the last keystroke is still inside the debounce
 * when Submit is pressed, the sealed version would be one pause behind the screen. So the flush
 * happens here and the transition only runs if it succeeded.
 *
 * ★ AND NOT WHILE A PICTURE IS STILL GOING IN. Send used to seal the draft without it, and the
 * claim then answered on an article already in review. So Send is HELD - focusable, its reason
 * said beside it - until every upload on the screen is in or has failed
 * (./editor/uploads-in-flight.ts). Waiting quietly was ruled out: the Storage SDK retries an
 * offline write for minutes.
 *
 * ★ PANES THAT ARE **PLATFORM WORK OWED** ARE NOT DRAWN: the moon phase for a chosen date, the
 * focus keyword, and "reply if you disagree" on a note. Each has no operation behind it, each is
 * named in the pane that would have carried it, and all three are in the notes under OPEN.
 *
 * ═══════════════════════════════════════════════════════════════════════════════════════════════
 * ★★ DESIGN 15 - THE PROTOTYPE'S EDITOR (the owner's live look at real cabinets, 2026-10-01)
 * ═══════════════════════════════════════════════════════════════════════════════════════════════
 * The screen is laid out as `moonx-authors-prototype/MoonX App.dc.html` draws it (`isEditor`): a top
 * row - "Articles › <the piece>", the save state, Send; under it one line naming what the checklist
 * still says "Not yet" to (./editor/MissingBeforeSending.tsx); then the WRITING CANVAS (cover, the
 * title large, the standfirst, the word count, the blocks) beside a SIDE PANEL OF TABS - Setup,
 * Preview, Search, Checklist, Version history and, once an editor has written any, Editor notes
 * (Preview only once a client opens a draft preview: C13, ./editor/draft-preview.ts). The panes
 * are the ones this file always drew; only where they sit changed. Every tab panel STAYS MOUNTED,
 * hidden with `display: none` (`keepMountedMode`), so every read, every pane's own state and every
 * effect runs as it did when all of them were on the page at once - an upload's, a check's, a read's.
 * The refusals a Send meets are said at the top, beside Send - the structural set included, which
 * the checklist pane used to carry far down the side. Words are the owner's plain words: no policy
 * version, no revision counter, no rule code, nothing about the platform's insides.
 */
import {
  Alert,
  Button,
  Divider,
  Stack,
  Tabs,
  Text,
  TextInput,
  Textarea,
  Title,
  VisuallyHidden,
} from '@mantine/core';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from '@tanstack/react-router';
import {
  CircleAlert,
  CircleCheck,
  Clock,
  Eye,
  FileClock,
  ImagePlus,
  ListChecks,
  MessageCircle,
  Search,
  Send,
  SlidersHorizontal,
} from '@moonx/ui';
import {
  EditorApiProvider,
  isStructuralLimitsRefusal,
  isTermsAcceptanceStale,
  useEditorApi,
} from '../../features/cabinet/api/editor-api';
import { createEditorApi } from '../../features/cabinet/api/editor-api-source';
import { articleWordCount } from '../../features/cabinet/api/editor-measure';
import { createQuarantineUploader } from '../../features/storage/create-quarantine-uploader';
import { emulatorImageUrl, servedUrlOnError } from '../../features/storage/emulator-image-url';
import { InAppAnchor } from '../../features/shell/InAppAnchor';
import { useNavigateToHref } from '../../features/shell/in-app-navigation';
import { useOptionalAuthSession } from '../../features/auth/session/AuthSessionProvider';
import { writesDraftsBeforeApproval } from '../../features/auth/session/session-state';
import { APPLICANT_ERROR_CODE } from '../../features/cabinet/api/problem';
import { APPLY_ROUTE } from '../apply';
import {
  ERROR_CODE,
  NotFoundFailure,
  describeProblem,
  hasErrorCode,
  isApiProblem,
} from '../../features/cabinet/api';
import { ErrorState, LoadingState, StatusPill } from '../../features/cabinet/components';
import { useAction, useAsync } from '../../features/cabinet/state';
import { formatCount, formatInstant } from '../../features/cabinet/format';
import { Trans, useTranslation } from '../../i18n';
import { videoEditorHrefFor } from '../videos/paths';
import { articlesT } from './articles-i18n';
import type { ArticlesKey, ArticlesT } from './articles-i18n';
import { AboutThisPiece, readingMinutes } from './editor/AboutThisPiece';
import { ArticlePreview } from './editor/ArticlePreview';
import { ArticleReadView } from './editor/ArticleReadView';
import { BlockList } from './editor/BlockList';
import { CalendarLinksPanel } from './editor/CalendarLinksPanel';
import { ConflictPanel } from './editor/ConflictPanel';
import { HistoryPanel, VersionInColumn } from './editor/HistoryPanel';
import { ImageUploadButton } from './editor/ImageUploadButton';
import { MissingBeforeSending } from './editor/MissingBeforeSending';
import { PreviewLinkNotice } from './editor/PreviewLinkNotice';
import { DRAFT_PREVIEW_SHOWN } from './editor/draft-preview';
import { ReviewNotes } from './editor/ReviewNotes';
import { SearchPanel } from './editor/SearchPanel';
import { SetupPanel } from './editor/SetupPanel';
import { ApprovalFirst, SubmitChecklist, SubmitRefusal } from './editor/SubmitChecklist';
import { TermsStaleNotice } from './editor/TermsStaleNotice';
import {
  addressCategoryRequiredMessage,
  addressCategoryRow,
  isAddressCategoryRefusal,
} from './editor/address';
import { checklistFor } from './editor/document';
import {
  UploadsInFlightProvider,
  createUploadsInFlight,
  useUploadsInFlight,
  useUploadsInFlightCount,
} from './editor/uploads-in-flight';
import { EditorUploaderProvider } from './editor/use-article-image-upload';
import { useEditorDraft } from './editor/use-editor-draft';
import { hasUnsavedWork, useLeaveWarning } from './editor/use-leave-warning';
import classes from './editor/editor.module.css';
import type { DraftBase } from './editor/document';
import type { OpenedVersion } from './editor/HistoryPanel';
import type { PieceContext } from './editor/piece-context';
import type { SaveState } from './editor/use-editor-draft';
import type {
  EditorApi,
  ReviewFinding,
  StructuralLimitFailure,
  StructuralRule,
  TagSuggestionGroup,
  TagSuggestions,
  WorkingCopy,
} from '../../features/cabinet/api/editor-api';
import type { QuarantineUploader } from '../../features/application/api/types';
import type { LucideIcon } from '@moonx/ui';

/**
 * The address, stated once. `$articleId` is TanStack Router's path-parameter spelling, and
 * `features/cabinet/routes.ts` hands `path` straight to `createRoute` (router.tsx:226-232), so the
 * descriptor and the router agree by construction.
 */
export const EDITOR_ROUTE_PATH = '/articles/$articleId/edit';

/** Where the article list sends a row. Exported so the list states the address once, not twice. */
export function editorHrefFor(articleId: string): string {
  return `/articles/${encodeURIComponent(articleId)}/edit`;
}

/** The list this screen is reached from - its descriptor's path (features/cabinet/routes.ts). */
const ARTICLES_HREF = '/articles';

/**
 * The side panel's tabs, in the prototype's order with Version history beside them. "Editor notes" is
 * drawn only once an editor has written a note (see `LoadedEditor`).
 */
export type EditorTab = 'setup' | 'preview' | 'search' | 'checklist' | 'history' | 'notes';

const EDITOR_TABS: readonly { value: EditorTab; label: ArticlesKey; icon: LucideIcon }[] = [
  { value: 'setup', label: 'editor.tabs.setup', icon: SlidersHorizontal },
  { value: 'preview', label: 'editor.tabs.preview', icon: Eye },
  { value: 'search', label: 'editor.tabs.search', icon: Search },
  { value: 'checklist', label: 'editor.tabs.checklist', icon: ListChecks },
  { value: 'history', label: 'editor.tabs.history', icon: FileClock },
  { value: 'notes', label: 'common.editorNotes', icon: MessageCircle },
];

/** The side panel's id: Mantine names each tab `<id>-tab-<value>` from it. */
const SIDE_PANEL_ID = 'editor-side';

function isEditorTab(value: string | null): value is EditorTab {
  return EDITOR_TABS.some((tab) => tab.value === value);
}

export interface ArticleEditorScreenProps {
  readonly articleId: string;
  /** Injected for tests and for the fixture-vs-network seam. Built here when absent. */
  readonly api?: EditorApi;
  /**
   * The writer a picture's bytes go through before the claim names them. Injected for tests; built
   * here when absent, by the same one live flag as the API - the fixture in the default build,
   * `firebase/storage` in a live one (features/storage/create-quarantine-uploader.ts).
   */
  readonly uploader?: QuarantineUploader;
}

/* ============================================================================================= */

export function ArticleEditorScreen({ articleId, api, uploader }: ArticleEditorScreenProps) {
  // One instance for the life of the screen: the fixture holds a revision counter, and rebuilding
  // it on every render would reset the author's draft to its opening state on every keystroke.
  const value = useMemo(() => api ?? createEditorApi(), [api]);
  // The same for the writer: a live one loads `firebase/storage` once, at its first write.
  const writer = useMemo(() => uploader ?? createQuarantineUploader(), [uploader]);
  // And one count of the pictures going in: each picture control counts itself in, Send waits on it.
  const [uploads] = useState(createUploadsInFlight);
  return (
    <EditorApiProvider value={value}>
      <EditorUploaderProvider value={writer}>
        <UploadsInFlightProvider value={uploads}>
          <EditorBody articleId={articleId} />
        </UploadsInFlightProvider>
      </EditorUploaderProvider>
    </EditorApiProvider>
  );
}

function EditorBody({ articleId }: { articleId: string }) {
  const { t } = useTranslation('articles');
  const api = useEditorApi();
  const navigate = useNavigateToHref();

  const copy = useAsync<WorkingCopy>(
    useCallback((signal) => api.getOwnArticle(articleId, signal), [api, articleId]),
    `editor:${articleId}`,
  );

  /* A VIDEO IS OPENED WHERE VIDEOS ARE EDITED. A push tap or an `enter?next=` hand-off names the
     piece by this address whatever its kind, and this screen would draw a video as an article -
     the editor and its read view alike. Replaced, so Back does not land here again. */
  const isVideo = copy.data?.kind === 'video';
  useEffect(() => {
    if (isVideo) {
      navigate(videoEditorHrefFor(articleId), { replace: true });
    }
  }, [isVideo, navigate, articleId]);

  if ((copy.status === 'loading' && copy.data === undefined) || isVideo) {
    return <LoadingState label={t('editor.opening')} rows={6} />;
  }
  if (copy.data === undefined) {
    return <ErrorState title={t('editor.didNotOpen')} error={copy.error} onRetry={copy.refetch} />;
  }
  /* KEYED ON THE ARTICLE. `useEditorDraft` seeds its state from the working copy it was given, and
     a hook cannot re-seed itself when the id changes; remounting is what makes moving between two
     drafts load the second rather than keep the first. */
  return <LoadedEditor key={articleId} initial={copy.data} />;
}

function LoadedEditor({ initial }: { initial: WorkingCopy }) {
  const { t, i18n } = useTranslation('articles');
  const api = useEditorApi();
  const state = useEditorDraft(initial);
  const { draft, base } = state;
  useLeaveWarning(hasUnsavedWork(state.saveState)); // R-018: ./editor/use-leave-warning.ts
  const readOnly = base.writableBy !== 'author';
  const uploads = useUploadsInFlight();
  const picturesGoingIn = useUploadsInFlightCount(uploads);
  /* ★ AN APPLICANT WRITES, AND SENDING WAITS FOR APPROVAL (design 25, D6): where Submit would be,
     "Your author application is approved first", a link to the profile
     (./editor/SubmitChecklist.tsx). Read off the session the app holds; an editor mounted with none
     (a spec) keeps its Submit. */
  const authSession = useOptionalAuthSession();
  const awaitingApproval = authSession !== null && writesDraftsBeforeApproval(authSession.state);

  const [resolving, setResolving] = useState<number | undefined>(undefined);
  /**
   * The last refused resolve, until it is dismissed or the next resolve starts. The refusal is
   * kept, not its sentence, so the sentence follows a change of the cabinet's language.
   */
  const [resolveRefusal, setResolveRefusal] = useState<{ readonly error: unknown } | undefined>(
    undefined,
  );
  const [previewUrl, setPreviewUrl] = useState<string | undefined>(undefined);
  // "Preview": the draft as the app and the website draw it (./editor/ArticlePreview.tsx).
  const [previewOpen, setPreviewOpen] = useState(false);
  /* ★ A VERSION READ FROM "VERSION HISTORY" OPENS IN THE CANVAS'S PLACE (design 24, T20), and the
     canvas is only hidden: the draft, its autosave and anything typed stay as they were until "Back
     to your draft" (./editor/HistoryPanel.tsx, `VersionInColumn`). */
  const [reading, setReading] = useState<OpenedVersion | undefined>(undefined);

  /* THE THREE REFERENCE READS. They are the editor's mirror of the platform and are read once per
     screen rather than per pane, so nothing on this page can disagree with anything else on it. */
  const rules = useAsync(
    useCallback((signal) => api.getStructuralRules(signal), [api]),
    'rules',
  );
  // The category names come in the cabinet's language, so a change of it reads them again.
  const categories = useAsync(
    useCallback((signal) => api.listEditorCategories(signal), [api]),
    `categories:${i18n.language}`,
  );
  const tags = useAsync(
    useCallback((signal) => api.suggestTags({}, signal), [api]),
    'tags',
  );
  // ...and asked again, with what is typed, as the author types (design 24, T6; `useTagSearch`).
  const tagSearch = useTagSearch(api);
  const dates = useAsync(
    useCallback((signal) => api.getPublishDateSuggestions({}, signal), [api]),
    'publish-dates',
  );
  const review = useAsync(
    useCallback((signal) => api.getArticleReview(base.articleId, signal), [api, base.articleId]),
    `review:${base.articleId}`,
  );
  const versions = useAsync(
    useCallback((signal) => api.listArticleVersions(base.articleId, signal), [api, base.articleId]),
    `versions:${base.articleId}`,
  );

  /* D21'S EIGHT, AS THE POLICY SENT THEM, AND THEN THE EDITOR'S OWN ROW (design 14, F8): the web
     address category, which the submit refuses by its own code before it asks any structural
     rule. It is drawn and counted where the eight are, so the count below names it too - and only
     once the pane is drawn, so the count never names a row the author cannot see. With NO category
     ticked it is left out: the categories row already asks for one, and ticking it fills this in
     (address.ts), so the count would name one missing step twice. */
  const categoryItems = categories.data?.items;
  const checklist = useMemo(
    () =>
      rules.data === undefined
        ? []
        : [
            ...checklistFor(draft, base, rules.data, t),
            ...(draft.categories.length === 0
              ? []
              : [
                  addressCategoryRow(
                    draft,
                    (code) =>
                      categoryItems?.find((category) => category.code === code)?.label ?? code,
                    t,
                  ),
                ]),
          ],
    [draft, base, rules.data, categoryItems, t],
  );

  const quoteRule = rules.data?.rules.find((rule) => rule.rule === 'quote_length');
  const categoryRule = rules.data?.rules.find((rule) => rule.rule === 'category_count');
  const coverRule = rules.data?.rules.find((rule) => rule.rule === 'cover_dimensions');
  const coverShortfall = readOnly ? null : coverShortfallNote(base, coverRule, t);

  /* THE SIDE PANEL'S TAB. A piece sent back with notes opens on them - they are why the author is
     here; anything else opens on Setup. "Editor notes" exists only once there are notes, so a choice
     of it before the review has been read shows Setup meanwhile. */
  const findings = review.data?.findings ?? [];
  const hasNotes = findings.length > 0;
  const openNotes = findings.filter((finding) => finding.disposition === 'open').length;
  const [chosenTab, setChosenTab] = useState<EditorTab>(() =>
    initial.status === 'changes_requested' ? 'notes' : 'setup',
  );
  const tab: EditorTab = chosenTab === 'notes' && !hasNotes ? 'setup' : chosenTab;
  const openChecklist = useCallback(() => {
    setChosenTab('checklist');
    // To the tab itself, so a keyboard lands where the detail is and the page scrolls to it.
    document.getElementById(`${SIDE_PANEL_ID}-tab-checklist`)?.focus();
  }, []);

  const submit = useAction(async () => {
    /* SAVE FIRST, AND ABANDON THE SUBMIT IF THE SAVE DID NOT LAND. See the header: the transition
       seals the working copy the autosave writes, not the screen — so a submit that ran over a
       refused save would seal a version one pause behind what the author is looking at. A conflict
       lands here too, and stopping is exactly right: the conflict pane is now up. */
    const saved = await state.saveNow();
    if (!saved) {
      return undefined;
    }
    // A picture chosen while that save flew holds the piece too: sealed now, it would go without it.
    if (uploads.count() > 0) {
      return undefined;
    }
    return base.status === 'changes_requested'
      ? await api.resubmitArticle(base.articleId)
      : await api.submitArticle(base.articleId);
  });

  const onSubmit = useCallback(
    (event?: { preventDefault: () => void }) => {
      /* ★ HELD WHILE A PICTURE IS GOING IN - read off the count at the click, not off the render,
         so a click racing an upload's start cannot seal the draft without it. Held, not disabled
         (ArticleRowActions.tsx's pattern): the button stays focusable, so its reason is reachable. */
      if (uploads.count() > 0) {
        event?.preventDefault();
        return;
      }
      // One send at a time: the preview's Submit must not start a second one beside it.
      if (submit.isRunning) {
        return;
      }
      void (async () => {
        const moved = await submit.run();
        if (moved !== undefined) {
          /* The article has changed status, so the working copy's `writableBy` has changed with it.
             Re-read rather than patch: a `TransitionResult` is the move and not the row. */
          const fresh = await api.getOwnArticle(base.articleId);
          state.adopt(fresh);
          review.refetch();
          versions.refetch();
        }
      })();
    },
    [uploads, submit, api, base.articleId, state, review, versions],
  );
  // Said only while it is true: not over a read-only article, nor once the piece is on its way.
  const sendHeld = !readOnly && !submit.isRunning && picturesGoingIn > 0;

  // The 422's WHOLE failure set, lifted out of the thrown refusal so the list renders every entry
  // at once. There is no separate state for it: the refusal lives on the action's error until the
  // next attempt clears it, which is the one place it can go stale from.
  const submitRefusal: readonly StructuralLimitFailure[] | undefined = isStructuralLimitsRefusal(
    submit.error,
  )
    ? submit.error.failures
    : undefined;

  const onResolve = useCallback(
    (finding: ReviewFinding) => {
      setResolving(finding.ordinal);
      setResolveRefusal(undefined);
      void api
        .resolveReviewFinding(base.articleId, finding.reviewSeq, finding.ordinal)
        .then(() => {
          /* The response carries the new `openBlockingCount`, which is the gate. Re-reading the
             record here keeps ONE source for the pane rather than splicing the wrapper members
             into a cached list — they are deliberately wrapper members and not members of the
             finding (authors-api.d.ts's FindingResolved). */
          review.refetch();
        })
        .catch((error: unknown) => {
          /* ★ EVERY REFUSAL IS SAID. Until wave 20 this chain had no `catch`, so a refused resolve
             was an unhandled rejection and the button simply stopped spinning. A 404 or a 409
             means the record moved under the author - a second tab, a closed review - so it is
             read again and the pane shows what is true now. */
          setResolveRefusal({ error });
          if (recordMoved(error)) {
            review.refetch();
          }
        })
        .finally(() => {
          setResolving(undefined);
        });
    },
    [api, base.articleId, review],
  );

  const onCheckSlug = useMemo(() => {
    const category = draft.addressCategory;
    if (category === null) {
      // Both query members are REQUIRED on this operation: an address is free or taken inside one
      // category, so there is no question to ask before the category is chosen.
      return undefined;
    }
    return async (slug: string): Promise<boolean> => {
      const answer = await api.checkArticleSlugAvailability(base.articleId, {
        slug,
        categoryCode: category,
      });
      return answer.available;
    };
  }, [api, base.articleId, draft.addressCategory]);

  const preview = useAction(async () => await api.mintDraftPreviewLink(base.articleId));

  const submitLabel =
    base.status === 'changes_requested'
      ? t('editor.submit.resubmit')
      : t('editor.submit.submitForReview');
  const closePreview = useCallback(() => {
    setPreviewOpen(false);
  }, []);
  // The preview closes first, so whatever the submit answers - a refusal, the checklist - is seen.
  // Held while a picture is going in, as the editor's Submit is: the preview stays open and says why.
  const submitFromPreview = useCallback(() => {
    if (uploads.count() > 0) {
      return;
    }
    setPreviewOpen(false);
    onSubmit();
  }, [uploads, onSubmit]);

  const words = articleWordCount(draft);
  const minutes = readingMinutes(words, draft.sourceLanguage);

  /* ★ A PIECE THE AUTHOR CANNOT CHANGE IS READ, NOT EDITED (the owner, 2026-10-05: "should not
     there be like a locked / another view/UI?" - he chose the read view): ./editor/ArticleReadView.tsx.
     Every hook above has run, so moving between the two - a Send that puts the piece in review - is
     one render to the next, and a verdict that opens it again brings the editor back. */
  if (readOnly) {
    return (
      <ArticleReadView
        base={base}
        draft={draft}
        categories={categories.data?.items ?? []}
        versions={versions}
        review={review.data}
        words={words}
        minutes={minutes}
      />
    );
  }

  return (
    <Stack gap="lg">
      {/* THE TOP ROW: where you are, whether it is saved, and Send. */}
      <div>
        <div className={classes.top}>
          <div className={classes.crumbs}>
            <InAppAnchor href={ARTICLES_HREF} className={classes.crumbLink} underline="hover">
              {t('common.articles')}
            </InAppAnchor>
            <span aria-hidden="true">›</span>
            {/* The page's one heading. The large title is the field on the canvas. */}
            <h1 className={classes.crumbTitle}>
              {draft.title.trim().length === 0 ? t('common.untitled') : draft.title}
            </h1>
          </div>
          <div className={classes.actions}>
            <StatusPill status={base.status} />
            <SaveStateLine saveState={state.saveState} updatedAt={base.updatedAt} />
            <Button
              variant="default"
              radius="xl"
              leftSection={<Eye size={15} aria-hidden />}
              // Not while the article is being sent: its Submit would send it a second time.
              disabled={submit.isRunning}
              onClick={() => {
                setPreviewOpen(true);
              }}
            >
              {t('editor.previewWindow.open')}
            </Button>
            {awaitingApproval ? (
              <ApprovalFirst />
            ) : (
              <Button
                radius="xl"
                leftSection={<Send size={15} aria-hidden />}
                loading={submit.isRunning}
                disabled={readOnly}
                data-disabled={sendHeld}
                aria-disabled={sendHeld || undefined}
                aria-describedby={sendHeld ? SEND_HELD_ID : undefined}
                onClick={onSubmit}
              >
                {submitLabel}
              </Button>
            )}
          </div>
        </div>
        <ArticlePreview
          opened={previewOpen}
          onClose={closePreview}
          draft={draft}
          categories={categories.data?.items ?? []}
          savedAt={base.updatedAt}
          submit={
            awaitingApproval
              ? undefined
              : {
                  label: submitLabel,
                  run: submitFromPreview,
                  heldReason: picturesGoingIn > 0 ? sendHeldReason(picturesGoingIn, t) : null,
                }
          }
        />
        {/* ALWAYS MOUNTED, EVEN EMPTY, as ./editor/ImageUploadButton.tsx's status line is: a
            live region that appears with its first message may never be announced. */}
        <Text id={SEND_HELD_ID} role="status" size="xs" c="dimmed" className={classes.heldReason}>
          {sendHeld ? sendHeldReason(picturesGoingIn, t) : ''}
        </Text>
      </div>

      {/* WHAT IS STILL MISSING, from the checklist's own rows - only where the piece can be sent. */}
      {readOnly ? null : <MissingBeforeSending rows={checklist} onOpenChecklist={openChecklist} />}

      {state.conflict === undefined ? null : (
        <ConflictPanel
          mine={draft}
          theirs={state.conflict}
          onTakeTheirs={state.takeServerCopy}
          onKeepMine={state.keepMyCopy}
        />
      )}

      {state.conflict === undefined && state.saveState === 'failed' ? (
        <Alert
          variant="light"
          role="alert"
          title={t('common.changeNotSaved')}
          styles={{
            root: {
              backgroundColor: 'oklch(var(--mx-danger) / 0.10)',
              border: '1px solid oklch(var(--mx-danger) / 0.45)',
            },
            title: { color: 'oklch(var(--mx-danger))' },
          }}
        >
          <Text size="sm">{describeProblem(state.error)}</Text>
        </Alert>
      ) : null}

      {/* STALE TERMS IS A DETOUR, NOT A MESSAGE: the reason and the way to /terms and back. The
          structural 422 has its own list (just below); every other refusal is said here. */}
      {isTermsAcceptanceStale(submit.error) ? (
        <TermsStaleNotice returnTo={editorHrefFor(base.articleId)} onClose={submit.clearError} />
      ) : null}

      {submit.error === undefined ||
      isStructuralLimitsRefusal(submit.error) ||
      isTermsAcceptanceStale(submit.error) ? null : (
        <Alert
          variant="light"
          role="alert"
          title={t('common.didNotGoThrough')}
          withCloseButton
          closeButtonLabel={t('common.dismiss')}
          onClose={submit.clearError}
          styles={{
            root: {
              backgroundColor: 'oklch(var(--mx-danger) / 0.10)',
              border: '1px solid oklch(var(--mx-danger) / 0.45)',
            },
            title: { color: 'oklch(var(--mx-danger))' },
          }}
        >
          <Text size="sm">{submitRefusalMessage(submit.error, t)}</Text>
          {hasErrorCode(submit.error, APPLICANT_ERROR_CODE.authorApprovalRequired) ? (
            <Text size="sm">
              <InAppAnchor href={APPLY_ROUTE.path} size="sm">
                {t('editor.approval.link')}
              </InAppAnchor>
            </Text>
          ) : null}
        </Alert>
      )}

      {/* THE WHOLE STRUCTURAL SET, beside Send that met it, named in the checklist's words. */}
      {submitRefusal === undefined ? null : (
        <SubmitRefusal failures={submitRefusal} rows={checklist} />
      )}

      <div className={classes.layout}>
        <div className={classes.grid}>
          {/* ------------------------------------------------------------- the writing canvas */}
          {reading === undefined ? null : (
            <VersionInColumn
              key={`${String(reading.revisionNo)}:${reading.language ?? ''}`}
              articleId={base.articleId}
              version={reading}
              backLabel={t('editor.backToDraft')}
              onBack={() => {
                setReading(undefined);
              }}
            />
          )}
          <div className={classes.canvas} hidden={reading !== undefined}>
            <section aria-label={t('editor.articleItself')}>
              <div className={classes.cover}>
                {base.coverUrl === null ? (
                  <ImagePlus size={22} className={classes.coverIcon} aria-hidden />
                ) : (
                  /* Through `emulatorImageUrl`, as every picture on this screen is: the API's
                     address is production Storage's, which the local stack does not serve
                     (features/storage/emulator-image-url.ts). ★ IN THE FRAME READERS GET (design
                     24, T9): 16:9, cropped from the center as the server crops every size. */
                  <div className={classes.coverFrame}>
                    <img
                      src={emulatorImageUrl(base.coverUrl)}
                      onError={servedUrlOnError(base.coverUrl)}
                      alt=""
                      className={classes.coverImage}
                    />
                  </div>
                )}
                {/* ★ THE BROWSER WRITES THE FILE, THEN THE CLAIM NAMES IT: "Nothing is ever POSTed
                    to us" (features/application/data/useUpload.ts:4-9). The claim's answer goes on
                    the draft through `setCover`, so the next autosave carries its `coverFileId` and
                    the canvas shows the new cover before that save has even gone. The writer is
                    provided above, by `ArticleEditorScreen`. */}
                <div className={classes.coverControl}>
                  <ImageUploadButton
                    articleId={base.articleId}
                    role="cover"
                    label={
                      base.coverUrl === null ? t('editor.cover.add') : t('editor.cover.change')
                    }
                    doneMessage={t('editor.cover.done')}
                    disabled={readOnly}
                    onClaimed={state.setCover}
                  />
                </div>
                {base.coverUrl === null ? (
                  <Text className={classes.coverNote}>{coverRuleNote(coverRule, t)}</Text>
                ) : (
                  <Text className={classes.coverNote}>
                    {t('editor.cover.size', {
                      width: base.coverWidthPx ?? '?',
                      height: base.coverHeightPx ?? '?',
                    })}
                    <br />
                    {t('editor.cover.anyShape')}
                  </Text>
                )}
                {/* Said the moment the picture is in, in the warning ink rather than grey: the
                    picture is kept, and the checklist still holds the submit (design 24, T9). */}
                {coverShortfall === null ? null : (
                  <Text className={classes.coverWarning}>
                    <CircleAlert size={13} className={classes.coverWarningIcon} aria-hidden />
                    {coverShortfall}
                  </Text>
                )}
              </div>

              <TextInput
                label={t('editor.titleField')}
                placeholder={t('editor.titlePlaceholder')}
                variant="unstyled"
                classNames={{ label: classes.srOnly, input: classes.bare }}
                styles={{ input: TITLE_FIELD }}
                disabled={readOnly}
                value={draft.title}
                onChange={(event) => {
                  const title = event.currentTarget.value;
                  state.edit((current) => ({ ...current, title }));
                }}
              />
              <Textarea
                label={t('editor.subtitleField')}
                description={t('editor.subtitleNote')}
                placeholder={t('editor.subtitleField')}
                autosize
                minRows={1}
                variant="unstyled"
                mt={6}
                classNames={{
                  label: classes.srOnly,
                  description: classes.srOnly,
                  input: classes.bare,
                }}
                styles={{ input: STANDFIRST_FIELD }}
                disabled={readOnly}
                value={draft.subtitle}
                onChange={(event) => {
                  const subtitle = event.currentTarget.value;
                  state.edit((current) => ({ ...current, subtitle }));
                }}
              />
              {/* The prototype's quiet line, from THE word count the checklist uses - only real
                  numbers, so no reading time before there is anything to read. */}
              <div className={classes.meta}>
                <span>{wordsLabel(words, t)}</span>
                {minutes === 0 ? null : <span>{t('common.minRead', { count: minutes })}</span>}
              </div>
            </section>

            <section aria-label={t('editor.body')}>
              <BlockList
                articleId={base.articleId}
                blocks={draft.blocks}
                disabled={readOnly}
                serverInstant={base.updatedAt}
                onChange={state.edit}
                quoteBodyMax={quoteRule?.bodyMax ?? quoteRule?.max ?? null}
                quoteAttributionMax={quoteRule?.attributionMax ?? null}
              />
            </section>
          </div>

          {/* --------------------------------------------------------------- the side panel */}
          <div className={classes.panel}>
            <Tabs
              id={SIDE_PANEL_ID}
              value={tab}
              onChange={(value) => {
                if (isEditorTab(value)) {
                  setChosenTab(value);
                }
              }}
              keepMounted
              keepMountedMode="display-none"
              unstyled
              classNames={{
                list: classes.tabList,
                tab: classes.tab,
                tabSection: classes.tabSection,
              }}
            >
              <Tabs.List aria-label={t('editor.tabs.label')}>
                {EDITOR_TABS.filter((entry) => tabShown(entry.value, hasNotes)).map(
                  ({ value, label, icon: Icon }) => (
                    <Tabs.Tab
                      key={value}
                      value={value}
                      leftSection={<Icon size={13} aria-hidden />}
                      rightSection={
                        value === 'notes' && openNotes > 0 ? (
                          <span className={classes.tabCount} aria-hidden="true">
                            {openNotes}
                          </span>
                        ) : undefined
                      }
                    >
                      {t(label)}
                      {value === 'notes' && openNotes > 0 ? (
                        <VisuallyHidden component="span">
                          {`, ${t('editor.tabs.open', { count: openNotes })}`}
                        </VisuallyHidden>
                      ) : null}
                    </Tabs.Tab>
                  ),
                )}
              </Tabs.List>

              <Tabs.Panel value="setup">
                <Stack gap="lg">
                  <SetupPanel
                    draft={draft}
                    disabled={readOnly}
                    categories={categories.data?.items ?? []}
                    tagSuggestions={(tagSearch.found ?? tags.data?.items ?? []).map(
                      (item) => item.tagText,
                    )}
                    // MoonX's own list; none until the server sends one (design 24, T6).
                    tagGroups={tags.data?.groups ?? NO_TAG_GROUPS}
                    onTagSearch={tagSearch.onTagSearch}
                    publishDates={dates.data?.items ?? []}
                    maxCategories={categoryRule?.max ?? null}
                    onChange={state.edit}
                    // A STORED VERSION FIXES THE LANGUAGE, and so does leaving `draft` (R-003).
                    languageFixed={
                      base.status !== 'draft' || (versions.data?.items.length ?? 0) > 0
                    }
                    languageRefused={state.languageRefused}
                  />
                  <Divider />
                  {/* THE PIECE'S CALENDAR EVENTS, with the other article settings. Not part of the
                      draft: the pane writes its own set (./editor/CalendarLinksPanel.tsx). A failed
                      suggestions read is an empty list to it, so it says there is no month rather
                      than reading forever. */}
                  <CalendarLinksPanel
                    articleId={base.articleId}
                    disabled={readOnly}
                    plannedGoLiveAt={draft.plannedGoLiveAt}
                    publishDates={dates.status === 'error' ? [] : dates.data?.items}
                  />
                  <Divider />
                  {/* The byline, the reading time and the way to the guidelines (R-033). */}
                  <AboutThisPiece draft={draft} />
                </Stack>
              </Tabs.Panel>

              {/* C13: not drawn until a client opens a draft preview (./editor/draft-preview.ts). */}
              {DRAFT_PREVIEW_SHOWN ? (
                <Tabs.Panel value="preview">
                  <Stack component="section" gap="md" aria-labelledby="editor-preview-title">
                    <Stack gap={4}>
                      <Title id="editor-preview-title" order={2} size="h5">
                        {t('editor.preview.title')}
                      </Title>
                      <Text className={classes.paneNote}>{t('editor.preview.note')}</Text>
                    </Stack>
                    <div>
                      <Button
                        variant="default"
                        radius="xl"
                        leftSection={<Eye size={15} aria-hidden />}
                        loading={preview.isRunning}
                        onClick={() => {
                          void (async () => {
                            const link = await preview.run();
                            if (link !== undefined) {
                              setPreviewUrl(link.url);
                            }
                          })();
                        }}
                      >
                        {t('editor.preview.getLink')}
                      </Button>
                    </div>
                    {previewUrl === undefined ? null : <PreviewLinkNotice url={previewUrl} />}
                  </Stack>
                </Tabs.Panel>
              ) : null}

              <Tabs.Panel value="search">
                <SearchPanel
                  draft={draft}
                  disabled={readOnly}
                  onCheckSlug={onCheckSlug}
                  onChange={state.edit}
                  // The address the platform holds, and why a save put the field back (R-019).
                  savedSlug={base.savedSlug}
                  saveRefusal={state.slugRefusal}
                  published={base.status === 'published' || base.status === 'unpublished'}
                />
              </Tabs.Panel>

              <Tabs.Panel value="checklist">
                {rules.data === undefined ? (
                  rules.status === 'error' ? (
                    <ErrorState
                      title={t('editor.checklist.didNotLoad')}
                      error={rules.error}
                      onRetry={rules.refetch}
                    />
                  ) : (
                    <LoadingState label={t('editor.checklist.reading')} rows={4} />
                  )
                ) : (
                  <SubmitChecklist
                    rows={checklist}
                    policyVersion={rules.data.policyVersion}
                    wordCountDefinition={rules.data.wordCountDefinition}
                    readOnly={readOnly} // no rows where the author cannot change the piece
                    refusal={undefined} // said at the top, beside Send (`SubmitRefusal` above)
                    awaitingApproval={awaitingApproval}
                  />
                )}
              </Tabs.Panel>

              {/* THE STORED VERSIONS (R-017): read-only, and "Keep this version" saves first, as
                  Send does - so it keeps what is on the screen, not the copy one pause behind it. */}
              <Tabs.Panel value="history">
                <HistoryPanel
                  articleId={base.articleId}
                  versions={versions}
                  canKeep={!readOnly}
                  saveFirst={state.saveNow}
                  reading={reading}
                  onRead={setReading}
                  canRead={picturesGoingIn === 0}
                />
              </Tabs.Panel>

              {review.data === undefined || !hasNotes ? null : (
                <Tabs.Panel value="notes">
                  <Stack gap="md">
                    {resolveRefusal === undefined ? null : (
                      <Alert
                        variant="light"
                        role="alert"
                        title={t('editor.resolveFailed')}
                        withCloseButton
                        closeButtonLabel={t('common.dismiss')}
                        onClose={() => {
                          setResolveRefusal(undefined);
                        }}
                        styles={{
                          root: {
                            backgroundColor: 'oklch(var(--mx-danger) / 0.10)',
                            border: '1px solid oklch(var(--mx-danger) / 0.45)',
                          },
                          title: { color: 'oklch(var(--mx-danger))' },
                        }}
                      >
                        <Text size="sm">{resolveRefusalMessage(resolveRefusal.error, t)}</Text>
                      </Alert>
                    )}
                    <ReviewNotes
                      review={review.data}
                      blocks={draft.blocks}
                      onResolve={onResolve}
                      resolvingOrdinal={resolving}
                      disabled={readOnly}
                    />
                  </Stack>
                </Tabs.Panel>
              )}
            </Tabs>
          </div>
        </div>
      </div>
    </Stack>
  );
}

/** The title field, drawn as the prototype's canvas heading: large, heavy, no box. */
export const TITLE_FIELD = {
  fontSize: 'clamp(24px, 3.4vw, 34px)',
  fontWeight: 800,
  letterSpacing: '-0.02em',
  lineHeight: 1.2,
  height: 'auto',
  minHeight: 0,
  padding: 0,
  backgroundColor: 'transparent',
  color: 'oklch(var(--mx-text))',
  opacity: 1,
} as const;

/** The standfirst under it: the reading size, in the quiet ink that reads on the card. */
export const STANDFIRST_FIELD = {
  fontSize: 17,
  lineHeight: 1.55,
  padding: 0,
  backgroundColor: 'transparent',
  color: 'oklch(var(--mx-m2))',
  opacity: 1,
} as const;

/**
 * "1 word", "1,234 words" - the count as the reading-time sentence prints it, in the cabinet's
 * language and never the browser's (`formatCount`, design 24 T16).
 */
export function wordsLabel(words: number, t: ArticlesT = articlesT()): string {
  return t('common.words', { count: words, value: formatCount(words) });
}

/**
 * The line under "Add cover image": the ruled minimum where the policy sent one - the same bound the
 * checklist's cover row reads - and the kinds of file a picture may be. No bound is written here;
 * with none sent, only the kinds are said.
 */
function coverRuleNote(rule: StructuralRule | undefined, t: ArticlesT): string {
  // The three `IMAGE_ACCEPT` names (./editor/ImageUploadButton.tsx), the kinds the platform stores.
  const kinds = t('editor.cover.kinds');
  const minWidth = rule?.minWidthPx ?? null;
  const minHeight = rule?.minHeightPx ?? null;
  return minWidth === null || minHeight === null
    ? kinds
    : t('editor.cover.minimum', { width: String(minWidth), height: String(minHeight), kinds });
}

/** The save state, beside Send: the words it always had, with a mark the eye finds. */
export function SaveStateLine({
  saveState,
  updatedAt,
}: {
  saveState: SaveState;
  updatedAt: string;
}) {
  const { t } = useTranslation('articles');
  const Mark =
    saveState === 'failed'
      ? CircleAlert
      : saveState === 'pending' || saveState === 'saving'
        ? Clock
        : CircleCheck;
  return (
    <span className={classes.saveState}>
      <Mark
        size={13}
        className={saveState === 'failed' ? classes.stateFailing : undefined}
        aria-hidden
      />
      {saveLabel(saveState, updatedAt, t)}
    </span>
  );
}

/**
 * What the author is told about a cover below the ruled minimum, or null when there is nothing to
 * say. ★ THE CLAIM ACCEPTS SUCH A COVER ON PURPOSE: the D21 floor "is checked at submit rather
 * than here: a draft may hold a cover that is still too small" (authors-api.yaml:1294-1296). So the
 * picture stays, and since design 24 (T9) the author is told at once and plainly that it is too
 * small to look sharp, rather than in grey that it will matter at submit. The bounds are the
 * policy's own, off `getStructuralRules`, as every number in the checklist is
 * (./editor/document.ts); with no policy or no measured size there is nothing honest to say, and
 * the checklist's row speaks instead.
 */
function coverShortfallNote(
  base: DraftBase,
  rule: StructuralRule | undefined,
  t: ArticlesT,
): string | null {
  const minWidth = rule?.minWidthPx ?? null;
  const minHeight = rule?.minHeightPx ?? null;
  const width = base.coverWidthPx;
  const height = base.coverHeightPx;
  if (base.coverUrl === null || minWidth === null || minHeight === null) {
    return null;
  }
  if (width === null || height === null || (width >= minWidth && height >= minHeight)) {
    return null;
  }
  return t('editor.cover.tooSmall', {
    width: String(width),
    height: String(height),
    minWidth: String(minWidth),
    minHeight: String(minHeight),
  });
}

/**
 * Did the refusal say the review record moved under the author? `resolveReviewFinding`'s 404 is "no
 * such review or finding on this article" and its 409 "the finding is already resolved or
 * dismissed, or the review it belongs to is closed" (authors-api.yaml) - both answered by a re-read.
 */
export function recordMoved(error: unknown): boolean {
  return (
    error instanceof NotFoundFailure ||
    (isApiProblem(error) && (error.status === 404 || error.status === 409))
  );
}

/**
 * What a refused "Mark as resolved" says. The operation declares 401, 403, 404 and 409. ★ THE
 * 409 CARRIES NO DOMAIN CODE - the platform answers `CONFLICT_UNSPECIFIED`, having no code that means
 * "already resolved" (src/modules/articles/services/article-review.service.ts) - so its sentence is
 * the contract's one meaning for a 409 HERE, in the pane's own words ("resolved", "set aside").
 * The owner 403 is said here because `describeProblem`'s sentence for that code is about a COMMENT.
 * `AUTHOR_DISABLED`, `AUTHOR_CAPABILITY_REQUIRED`, a 401 and no network are `describeProblem`'s own.
 */
export function resolveRefusalMessage(
  error: unknown,
  t: ArticlesT,
  context: PieceContext = '',
): string {
  if (hasErrorCode(error, ERROR_CODE.notArticleOwner)) {
    return t('editor.resolveRefusal.notYours', { context });
  }
  if (isApiProblem(error) && error.status === 409) {
    return t('editor.resolveRefusal.alreadyClosed');
  }
  if (recordMoved(error)) {
    return t('editor.resolveRefusal.gone');
  }
  return describeProblem(error, context);
}

/**
 * What a refused Send says, in the alert under the header. `submitArticle` and `resubmitArticle`
 * declare 403 (`NOT_ARTICLE_OWNER`, `AUTHOR_CAPABILITY_REQUIRED`), 409 (`WRONG_FROM_STATE`,
 * `TERMS_ACCEPTANCE_STALE`, and on the resubmit `BLOCKING_ISSUES_UNRESOLVED` and
 * `NOTHING_CHANGED_SINCE_REVIEW`), 422 (`STRUCTURAL_LIMITS_FAILED`, `ADDRESS_CATEGORY_REQUIRED`) and
 * 429. Stale terms and the structural set have panes of their own and never reach here.
 *
 * ★ `ADDRESS_CATEGORY_REQUIRED` IS SAID IN PLAIN WORDS (design 14, F8): the cabinet walk found the
 * server's `detail` on screen - "choose which of the categories ... (filed under moon)" - which
 * names category codes and no control. The owner 403 is said here because `describeProblem`'s
 * sentence for that code is about a COMMENT, as the other panes on this screen say it for themselves.
 * `AUTHOR_CAPABILITY_REQUIRED`, `AUTHOR_DISABLED`, a 429, a 5xx and no network are
 * `describeProblem`'s own - and design 25's `AUTHOR_APPROVAL_REQUIRED`, an applicant's 403, whose
 * alert also links to the author profile. The three 409s this screen has no words of its own for
 * still fall through to the server's `detail`, as before: words for them are a decision not taken
 * here.
 */
export function submitRefusalMessage(
  error: unknown,
  t: ArticlesT,
  context: PieceContext = '',
): string {
  if (isAddressCategoryRefusal(error)) {
    return addressCategoryRequiredMessage(t);
  }
  if (hasErrorCode(error, ERROR_CODE.notArticleOwner)) {
    return t('editor.submit.notYours', { context });
  }
  return describeProblem(error, context);
}

function saveLabel(saveState: SaveState, updatedAt: string, t: ArticlesT): string {
  switch (saveState) {
    case 'saving':
      return t('editor.save.saving');
    case 'pending':
      return t('editor.save.pending');
    case 'saved':
      return t('editor.save.saved');
    case 'failed':
      return t('editor.save.failed');
    default:
      return t('editor.save.lastSaved', { when: formatInstant(updatedAt) });
  }
}

/**
 * ★ THE TAG FIELD ASKS AGAIN AS THE AUTHOR TYPES (design 24, T6). The read with nothing typed
 * answers the twenty tags other authors use most, and MoonX's own list as `groups`; typing used to
 * filter only those twenty. Now a pause in the typing asks `suggestTags` with `q`, so a tag other
 * authors used that is not among the twenty still comes up, and its answer is what the Setup pane
 * offers beside the list. A timer and never a clock read (C11); a newer question cancels the older
 * one, so a slow answer cannot overwrite a newer one; an empty field goes back to the first answer
 * without asking. A failed search keeps what is offered: the field still takes any tag.
 */
export const TAG_SEARCH_PAUSE_MS = 250;

/** `q` is at most 40 characters (`suggestTagsQuery`), the longest a tag may be. */
const TAG_QUERY_MAX = 40;

/** One empty list, so the Setup pane is not handed a new one on every render. */
export const NO_TAG_GROUPS: readonly TagSuggestionGroup[] = [];

export interface TagSearch {
  /** What the last search answered, or `undefined` while nothing is typed: the first read's then. */
  readonly found: TagSuggestions['items'] | undefined;
  readonly onTagSearch: (query: string) => void;
}

export function useTagSearch(api: EditorApi): TagSearch {
  const [found, setFound] = useState<TagSuggestions['items'] | undefined>(undefined);
  const pending = useRef<{ timer?: ReturnType<typeof setTimeout>; controller?: AbortController }>(
    {},
  );

  const stop = useCallback(() => {
    clearTimeout(pending.current.timer);
    pending.current.controller?.abort();
    pending.current = {};
  }, []);
  // A screen that has gone is asked nothing more and answered into by nothing.
  useEffect(() => stop, [stop]);

  const onTagSearch = useCallback(
    (query: string) => {
      stop();
      const typed = query.trim();
      if (typed.length === 0) {
        setFound(undefined);
        return;
      }
      if (typed.length > TAG_QUERY_MAX) {
        setFound([]); // No tag is that long, so nobody has used one.
        return;
      }
      const timer = setTimeout(() => {
        const controller = new AbortController();
        pending.current = { controller };
        void api
          .suggestTags({ q: typed }, controller.signal)
          .then((answer) => {
            if (!controller.signal.aborted) {
              setFound(answer.items);
            }
          })
          .catch(() => {
            // Cancelled by a newer question, or failed: what is offered stays.
          });
      }, TAG_SEARCH_PAUSE_MS);
      pending.current = { timer };
    },
    [api, stop],
  );

  return { found, onTagSearch };
}

/** The line beside Send saying why it is held. The button names it as its description. */
const SEND_HELD_ID = 'editor-send-held';

/* One image is said in its own words, so the counted form is only asked for two or more. */
function sendHeldReason(picturesGoingIn: number, t: ArticlesT): string {
  return picturesGoingIn === 1
    ? t('editor.sendHeldOne')
    : t('editor.sendHeld', { count: picturesGoingIn });
}

/* ============================================================================================= */

/**
 * The route component the descriptor names — a `FunctionComponent` with no props, because
 * `CabinetRoute.Component` is one (features/cabinet/routes.ts:36-44).
 *
 * ★ IT IS THE ONLY PART OF THIS SCREEN THAT KNOWS ABOUT THE ROUTER. `useParams({ strict: false })`
 * is the loose read, which is what an app whose route tree is built from a descriptor list can
 * use; the screen itself takes an id and is testable without a router around it. When the two are
 * separated the other way — the screen reading the address — a unit test has to mount a router to
 * render a text field.
 */
export function EditorRoute() {
  const { t } = useTranslation('articles');
  const params = useParams({ strict: false }) as Readonly<Record<string, string | undefined>>;
  const articleId = params['articleId'];

  if (articleId === undefined || articleId.length === 0) {
    return (
      <Stack gap="sm">
        <Title order={1} size="h3">
          {t('editor.noArticle.title')}
        </Title>
        <Text size="sm">
          {/* The link's words are the catalogue's, put in its place by `Trans`. */}
          <Trans
            t={t}
            i18nKey="editor.noArticle.text"
            components={{ anchor: <InAppAnchor href="/articles">{null}</InAppAnchor> }}
          />
        </Text>
      </Stack>
    );
  }

  return <ArticleEditorScreen articleId={articleId} />;
}

/**
 * Which side-panel tabs are drawn: "Editor notes" once there are notes, and Preview only once a
 * client opens a draft preview (C13, ./editor/draft-preview.ts).
 */
function tabShown(value: EditorTab, hasNotes: boolean): boolean {
  return (value !== 'notes' || hasNotes) && (value !== 'preview' || DRAFT_PREVIEW_SHOWN);
}
